/**
 * Pure diagnostic word-list visibility helpers (including makeup resume prefix hiding).
 * Kept separate from session-feedback-utils so Vitest can cover them without loading the
 * full feedback form module graph.
 */

export type DiagnosticWordListContent = {
  id: string;
  format: string;
  maxErrorsAllowed: number;
};

export type DiagnosticWordListFormWatch = {
  watch: (path: string) => unknown;
};

/** maxErrorsAllowed >= 100 means unlimited (letter names / sounds). */
export const isDiagnosticContentWithUnlimitedErrors = (content: DiagnosticWordListContent) =>
  content.format === 'diagnostic_word_list' && content.maxErrorsAllowed >= 100;

/**
 * Checks if the diagnostic word list is visible based on the assessment results.
 * A list is visible if either:
 * - there's no previous error-limited list, so this is the first one
 * - the previous list did not exceed its max errors allowed
 * - OR if any previous list exceeded max errors, this level should be hidden
 */
export const isDiagnosticWordListVisible = ({
  sortedAssessmentContents,
  resultIndex,
  assessmentIndex,
  form,
}: {
  sortedAssessmentContents: DiagnosticWordListContent[];
  resultIndex: number;
  assessmentIndex: number;
  form: DiagnosticWordListFormWatch;
}) => {
  if (resultIndex === 0) {
    return true;
  }

  for (let i = 0; i < resultIndex; i++) {
    const prevContent = sortedAssessmentContents[i];

    if (isDiagnosticContentWithUnlimitedErrors(prevContent)) {
      continue;
    }

    const prevListNoError = form.watch(`assessmentResults.${assessmentIndex}.results.${i}.noErrorsMadeInWordList`);
    const prevListIncorrectWords = form.watch(`assessmentResults.${assessmentIndex}.results.${i}.incorrectWords`) as
      | string[]
      | undefined;

    const prevListFilledIn = prevListNoError === true || (prevListIncorrectWords?.length ?? 0) > 0;

    if (prevListFilledIn && !prevListNoError && (prevListIncorrectWords?.length ?? 0) > prevContent.maxErrorsAllowed) {
      return false;
    }
  }

  const prevContent = sortedAssessmentContents[resultIndex - 1];
  if (isDiagnosticContentWithUnlimitedErrors(prevContent)) {
    return true;
  }

  const prevListNoError = form.watch(
    `assessmentResults.${assessmentIndex}.results.${resultIndex - 1}.noErrorsMadeInWordList`,
  );
  const prevListIncorrectWords = form.watch(
    `assessmentResults.${assessmentIndex}.results.${resultIndex - 1}.incorrectWords`,
  ) as string[] | undefined;

  const prevListFilledIn = prevListNoError === true || (prevListIncorrectWords?.length ?? 0) > 0;
  if (!prevListFilledIn) {
    return false;
  }
  if (prevListNoError) {
    return true;
  }
  if ((prevListIncorrectWords?.length ?? 0) > prevContent.maxErrorsAllowed) {
    return false;
  }

  return true;
};

/**
 * Diagnostic word lists shown in the feedback form. Hides lists completed in a prior
 * incomplete diagnostic (makeup resume), then applies normal progression visibility.
 */
export function getVisibleDiagnosticAssessmentContents<T extends DiagnosticWordListContent>({
  sortedAssessmentContents,
  assessmentIndex,
  form,
  priorCompletedContentIds = [],
}: {
  sortedAssessmentContents: T[];
  assessmentIndex: number;
  form: DiagnosticWordListFormWatch;
  priorCompletedContentIds?: string[];
}): T[] {
  const priorCompleted = new Set(priorCompletedContentIds);
  return sortedAssessmentContents.filter((content, index) => {
    if (content.format !== 'diagnostic_word_list') {
      return true;
    }
    if (priorCompleted.has(content.id)) {
      return false;
    }
    return isDiagnosticWordListVisible({
      sortedAssessmentContents,
      resultIndex: index,
      assessmentIndex,
      form,
    });
  });
}

export function getLastVisibleDiagnosticContentId(
  visibleAssessmentContents: DiagnosticWordListContent[],
): string | undefined {
  return visibleAssessmentContents.findLast((content) => content.format === 'diagnostic_word_list')?.id;
}

/** Extract "1" from names like "List 1: Closed Syllables (CVC)". */
export function getDiagnosticWordListNumber(contentName: string): number | null {
  const match = /^List (\d+):/.exec(contentName);
  if (!match) {
    return null;
  }
  return Number.parseInt(match[1], 10);
}

export type MakeupDiagnosticResumeContent = {
  id: string;
  name: string;
  format: string;
  internalOrder: number;
};

/**
 * Callout for a makeup diagnostic whose previous attended session stopped after at least one
 * numbered word list. The resume list is the first word list that was not completed then, so it
 * matches the list the feedback form shows after hiding that prefix.
 */
export function getMakeupDiagnosticResumeMessage(
  priorCompletedContentIds: readonly string[],
  diagnosticContents: readonly MakeupDiagnosticResumeContent[],
): string | null {
  if (priorCompletedContentIds.length === 0) {
    return null;
  }

  const completed = new Set(priorCompletedContentIds);
  const wordLists = diagnosticContents
    .filter((content) => content.format === 'diagnostic_word_list')
    .toSorted((a, b) => a.internalOrder - b.internalOrder);

  const resumeIndex = wordLists.findIndex((content) => !completed.has(content.id));
  if (resumeIndex < 0) {
    return null;
  }

  const resumeList = getDiagnosticWordListNumber(wordLists[resumeIndex].name);
  const lastCompletedNumberedList = wordLists
    .slice(0, resumeIndex)
    .findLast((content) => getDiagnosticWordListNumber(content.name) != null);
  if (resumeList == null || !lastCompletedNumberedList) {
    return null;
  }

  return `The student did not complete the Diagnostic Assessment in their previous class. Please resume at List ${resumeList}.`;
}
